import { useState, useEffect, useCallback } from "react";
import {
  Typography, Table, Tag, Select, Button, Space, Card, Row, Col,
  Statistic, Badge, Tooltip,
} from "antd";
import {
  LoginOutlined, SearchOutlined, FilePdfOutlined, ReloadOutlined,
  FileExcelOutlined,
} from "@ant-design/icons";
import {
  ResponsiveContainer, ComposedChart, Line, Bar, XAxis, YAxis,
  Tooltip as RTooltip, CartesianGrid, Legend,
} from "recharts";
import {
  getPortalActivityGrouped, getPortalActivityStats,
  getPortalActivityDailyStats, exportPortalActivity,
} from "../../api/portalService";

const { Title, Text } = Typography;
const { Option } = Select;

const ACTION_CONFIG = {
  login:    { color: "blue",   icon: <LoginOutlined />,   label: "Login" },
  search:   { color: "green",  icon: <SearchOutlined />,  label: "Búsqueda" },
  open_pdf: { color: "orange", icon: <FilePdfOutlined />, label: "PDF abierto" },
};

const formatDetail = (action, details) => {
  if (!details) return null;
  if (action === "search") {
    const parts = [];
    if (details.year) parts.push(`Año ${details.year}`);
    if (details.month) parts.push(`Mes ${details.month}`);
    if (details.repertoryNumber) parts.push(`Rep. ${details.repertoryNumber}`);
    if (details.partyName) parts.push(`"${details.partyName}"`);
    if (details.actType) parts.push(details.actType);
    if (details.totalResults !== undefined) parts.push(`→ ${details.totalResults} resultado(s)`);
    return parts.length ? parts.join(" · ") : "Sin filtros";
  }
  if (action === "open_pdf") return details.title || details.fileName || null;
  return null;
};

const ExpandedActions = ({ actions }) => (
  <div style={{ padding: "8px 16px 8px 48px" }}>
    <table style={{ width: "100%", borderCollapse: "collapse" }}>
      <thead>
        <tr style={{ borderBottom: "1px solid #f0f0f0" }}>
          {["Hora", "Acción", "Detalle"].map(h => (
            <th key={h} style={{ textAlign: "left", padding: "4px 12px 4px 0", color: "#888", fontWeight: 500, fontSize: 12 }}>{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {[...actions].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt)).map((a, i) => (
          <tr key={i} style={{ borderBottom: "1px solid #fafafa" }}>
            <td style={{ padding: "5px 12px 5px 0", fontSize: 12, color: "#555", whiteSpace: "nowrap" }}>
              {a.createdAt ? new Date(a.createdAt).toLocaleTimeString("es-CL", { hour: "2-digit", minute: "2-digit", second: "2-digit" }) : "—"}
            </td>
            <td style={{ padding: "5px 12px 5px 0" }}>
              <Tag color={ACTION_CONFIG[a.action]?.color} icon={ACTION_CONFIG[a.action]?.icon} style={{ fontSize: 11 }}>
                {ACTION_CONFIG[a.action]?.label || a.action}
              </Tag>
            </td>
            <td style={{ padding: "5px 0", fontSize: 12, color: "#333" }}>
              {formatDetail(a.action, a.details) || <Text type="secondary">—</Text>}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const ActivityChart = ({ data }) => {
  if (!data || data.length === 0) return null;
  const chartData = data.map(d => ({
    fecha: d.date.slice(5),   // MM-DD
    Logins: d.login || 0,
    Búsquedas: d.search || 0,
    PDFs: d.open_pdf || 0,
  }));
  return (
    <Card
      title={<Text strong>Actividad últimos 30 días</Text>}
      style={{ marginBottom: 24, borderRadius: 8 }}
      size="small"
    >
      <ResponsiveContainer width="100%" height={200}>
        <ComposedChart data={chartData} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis dataKey="fecha" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
          <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
          <RTooltip />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          <Bar dataKey="Logins" fill="#1890ff" radius={[3,3,0,0]} maxBarSize={18} />
          <Bar dataKey="Búsquedas" fill="#52c41a" radius={[3,3,0,0]} maxBarSize={18} />
          <Line type="monotone" dataKey="PDFs" stroke="#fa8c16" strokeWidth={2} dot={false} />
        </ComposedChart>
      </ResponsiveContainer>
    </Card>
  );
};

const PortalActivityLog = () => {
  const [data, setData]           = useState([]);
  const [total, setTotal]         = useState(0);
  const [loading, setLoading]     = useState(false);
  const [exporting, setExporting] = useState(false);
  const [page, setPage]           = useState(1);
  const [actionFilter, setActionFilter] = useState(undefined);
  const [expandedKeys, setExpandedKeys] = useState([]);
  const [stats, setStats]         = useState({ login: 0, search: 0, open_pdf: 0 });
  const [chartData, setChartData] = useState([]);

  const fetchStats = useCallback(async () => {
    try {
      const [s, c] = await Promise.all([
        getPortalActivityStats(),
        getPortalActivityDailyStats(30),
      ]);
      setStats(s.data);
      setChartData(c.data.days || []);
    } catch {}
  }, []);

  const fetchGroups = useCallback(async (p = 1, action = actionFilter) => {
    setLoading(true);
    try {
      const params = { page: p, limit: 20 };
      if (action) params.action = action;
      const { data: res } = await getPortalActivityGrouped(params);
      setData(res.results || []);
      setTotal(res.totalResults || 0);
      setPage(p);
      setExpandedKeys([]);
    } finally {
      setLoading(false);
    }
  }, [actionFilter]);

  useEffect(() => { fetchGroups(1); fetchStats(); }, [fetchGroups, fetchStats]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await exportPortalActivity();
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url; a.download = 'actividad-portal.xlsx'; a.click();
      URL.revokeObjectURL(url);
    } finally { setExporting(false); }
  };

  const rowKey = (row) => `${row._id?.userId}-${row._id?.date}`;

  const actionSummary = (actions) => {
    const counts = actions.reduce((acc, a) => { acc[a.action] = (acc[a.action] || 0) + 1; return acc; }, {});
    return Object.entries(counts).map(([action, count]) => (
      <Tag key={action} color={ACTION_CONFIG[action]?.color} icon={ACTION_CONFIG[action]?.icon} style={{ fontSize: 11 }}>
        {ACTION_CONFIG[action]?.label} ×{count}
      </Tag>
    ));
  };

  const columns = [
    {
      title: "Fecha", key: "date", width: 110,
      render: (_, row) => (
        <Text style={{ fontSize: 13 }}>
          {row._id?.date ? new Date(row._id.date + "T00:00:00").toLocaleDateString("es-CL") : "—"}
        </Text>
      ),
    },
    {
      title: "Usuario", key: "user",
      render: (_, row) => (
        <div>
          <Text strong style={{ fontSize: 13 }}>{row.userName || "—"}</Text>
          <br />
          <Text type="secondary" style={{ fontSize: 11 }}>{row.userEmail}</Text>
        </div>
      ),
    },
    {
      title: "Acciones del día", key: "summary",
      render: (_, row) => <Space size={4} wrap>{actionSummary(row.actions || [])}</Space>,
    },
    {
      title: "Total", key: "count", width: 70,
      render: (_, row) => <Badge count={row.count} color="#1d3557" style={{ fontSize: 12 }} />,
    },
    {
      title: "Último acceso", key: "lastAt", width: 140,
      render: (_, row) => row.lastAt
        ? new Date(row.lastAt).toLocaleString("es-CL", { dateStyle: "short", timeStyle: "short" })
        : "—",
    },
    {
      title: "IP", dataIndex: "ip", key: "ip", width: 120,
      render: (ip) => <Text type="secondary" style={{ fontSize: 11 }}>{ip || "—"}</Text>,
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 20, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
        <Title level={3} style={{ margin: 0, color: "#1d3557" }}>Actividad Portal Clientes</Title>
        <Space>
          <Tooltip title="Exportar a Excel">
            <Button
              icon={<FileExcelOutlined />}
              loading={exporting}
              onClick={handleExport}
              style={{ color: "#217346", borderColor: "#217346" }}
            >
              Exportar Excel
            </Button>
          </Tooltip>
          <Button icon={<ReloadOutlined />} onClick={() => { fetchGroups(1); fetchStats(); }}>
            Actualizar
          </Button>
        </Space>
      </div>

      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={8}>
          <Card size="small">
            <Statistic title="Logins" value={stats.login} prefix={<LoginOutlined />} valueStyle={{ color: "#1890ff" }} />
          </Card>
        </Col>
        <Col xs={8}>
          <Card size="small">
            <Statistic title="Búsquedas" value={stats.search} prefix={<SearchOutlined />} valueStyle={{ color: "#52c41a" }} />
          </Card>
        </Col>
        <Col xs={8}>
          <Card size="small">
            <Statistic title="PDFs abiertos" value={stats.open_pdf} prefix={<FilePdfOutlined />} valueStyle={{ color: "#fa8c16" }} />
          </Card>
        </Col>
      </Row>

      <ActivityChart data={chartData} />

      <Space style={{ marginBottom: 16 }}>
        <Select
          placeholder="Filtrar por acción"
          allowClear
          style={{ width: 180 }}
          value={actionFilter}
          onChange={(v) => { setActionFilter(v); fetchGroups(1, v); }}
        >
          <Option value="login">Login</Option>
          <Option value="search">Búsqueda</Option>
          <Option value="open_pdf">PDF abierto</Option>
        </Select>
      </Space>

      <Table
        dataSource={data}
        columns={columns}
        rowKey={rowKey}
        loading={loading}
        expandable={{
          expandedRowKeys: expandedKeys,
          onExpandedRowsChange: setExpandedKeys,
          expandedRowRender: (row) => <ExpandedActions actions={row.actions || []} />,
          rowExpandable: (row) => (row.actions?.length || 0) > 0,
        }}
        pagination={{
          current: page, pageSize: 20, total,
          onChange: (p) => fetchGroups(p),
          showTotal: (t) => `${t} grupos`,
        }}
        size="small"
      />
    </div>
  );
};

export default PortalActivityLog;
